import { type ReactElement, useMemo } from 'react';

import classNames from 'classnames';

import type { FilesProxySchemaWithId } from '@iconik-io/iconik-api/src/services/files';
import {
  UPLOADER_STATUS_COMPLETED,
  UPLOADER_STATUS_FAILED,
} from '@iconik-io/uploader/src/state';

import DebugDumpEntity from '../../components/debug_dump_entity/debug_dump_entity';
import Ellipsis from '../../components/ellipsis/ellipsis';
import { MANAGE_PROXY } from '../../constants/help_urls';
import { downloadProxyById } from '../../ducks/files/thunks';
import { useAppDispatch } from '../../store/react_redux_typed';
import type { AppDispatch } from '../../store/types';
import IconButton from '../../ui/icon_button/icon_button';
import Label from '../../ui/label/label';
import OpenContextMenuButton from '../../ui/table/open_context_menu_button/open_context_menu_button';
import { Progress, StorageItem, Title } from './cell_components';
import ContextMenu from './context_menu_proxy';
import TableGroup from './table_group';
import type { FilesGridColumn, Uploading } from './types';

import styles from './asset_files.module.css';

export type TableProxiesProps = {
  items: FilesProxySchemaWithId[];
  canDownload: boolean;
  assetId: string;
  title: string;
  uploading: Uploading | undefined;
  selectedIds: string[];
  onSelectIds: (ids: string[]) => void;
  readOnly?: boolean;
};

type ColumnProps = Pick<
  TableProxiesProps,
  'uploading' | 'readOnly' | 'canDownload' | 'assetId'
>;

const getColumns = (
  props: ColumnProps,
  dispatch: AppDispatch,
): FilesGridColumn<FilesProxySchemaWithId>[] => {
  if (
    props.uploading &&
    props.uploading.progress !== 100 &&
    props.uploading.status !== UPLOADER_STATUS_COMPLETED
  ) {
    return [
      {
        name: 'name',
        fullWidth: true,
        component: () =>
          props.uploading ? (
            <Progress
              value={props.uploading.progress}
              name={props.uploading.name}
              error={props.uploading.status === UPLOADER_STATUS_FAILED}
            />
          ) : (
            ''
          ),
      },
    ];
  }
  return [
    {
      name: 'format',
      component: ({ item }) => {
        const title = item.is_multichannel
          ? 'MULTI-CHANNEL AUDIO PROXY'
          : 'PROXY';
        return <Title iconName="file_proxy" head={title} deck={item.format} />;
      },
    },
    {
      name: 'name',
      component: ({ item }) => (
        <>
          <DebugDumpEntity entityKey="proxies" id={item.id} />
          <div className={styles.fileset} key={item.id}>
            <Ellipsis
              tooltipText={item.name}
              componentProps={{ className: styles.ellipsis_name }}
            >
              {item.name}
            </Ellipsis>
            <div className={styles.td_columns}>
              {item.status === 'AWAITED' ? (
                <Label
                  className={classNames(styles.status_label, styles.proxy)}
                  variant="warning"
                >
                  WAITING
                </Label>
              ) : undefined}
            </div>
          </div>
        </>
      ),
    },
    {
      name: 'storage_id',
      component: ({ item }) => (
        <>
          <DebugDumpEntity entityKey="storages" id={item.storage_id ?? 'N/A'} />
          <StorageItem
            iconName="storage_cloud"
            storageId={item.storage_id}
            readOnly={props.readOnly}
          />
        </>
      ),
    },
    {
      name: 'asset_id',
      component: ({ item }) =>
        !props.readOnly && props.canDownload ? (
          <div className={styles.cell_buttons}>
            <IconButton
              variant="white"
              iconName="load_down"
              onClick={() =>
                dispatch(downloadProxyById(props.assetId, item.id))
              }
              className={styles.icon_button}
              aria-label="Download proxy"
            />
          </div>
        ) : null,
    },
    {
      name: 'menu',
      component: () => <OpenContextMenuButton />,
    },
  ];
};

export default function TableProxies(
  props: TableProxiesProps,
): ReactElement | null {
  const { items, title, uploading, readOnly, canDownload, assetId } = props;
  const dispatch = useAppDispatch();

  const columns = useMemo(
    () => getColumns({ uploading, readOnly, canDownload, assetId }, dispatch),
    [uploading, readOnly, canDownload, assetId],
  );

  if (!items.length) {
    return null;
  }

  return (
    <TableGroup<FilesProxySchemaWithId>
      title={title}
      items={items}
      contextMenu={ContextMenu}
      columns={columns}
      help={MANAGE_PROXY}
      uiKey="proxies"
      selectedIds={props.selectedIds}
      onSelectIds={props.onSelectIds}
    />
  );
}
